<template>
  <div v-if="modals.length">
    {{ modals.length }}
    <component
      :is="component"
      v-for="{ id, component, props, options } of modals"
      v-show="options.show"
      :key="id"
      :ref="
        (el) => {
          divs[id] = el;
        }
      "
      v-bind="props"
      @close="onclose"
      @click="(e) => handleBackdrop(e, id, options.backdrop)"
    />
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, getCurrentInstance, markRaw } from "vue";
import { addClass, removeClass } from "./utils";

const isPromise = (promise) =>
  Object.prototype.toString.call(promise) === "[object Promise]" ||
  promise instanceof Promise;

const DEFAULT_OPTIONS = {
  show: true,
  backdrop: true,
  destroy: false,
};

const NON_TRANSITION_ERR =
  "this modal item is not a Vue component, you should use `transition` component and emit `after-leave` event";

export type Nullable<T> = T | null;

export type iModals = {
  id: string;
  component: Promise<any>;
  props: any;
  options: any;
};

export default defineComponent({
  setup() {
    const divs = ref([]);
    return {
      divs,
    };
  },
  data() {
    const currModal: iModals | null = <iModals>{};
    let dataResolve: any;
    return {
      modals: new Array<iModals>(),
      currModal: currModal as iModals | undefined,
      dataResolve,
    };
  },
  computed: {
    currModalId(): string | undefined {
      return this.currModal && this.currModal.id;
    },
  },
  watch: {
    // eslint-disable-next-line space-before-function-paren
    currModal: function (modal) {
      (modal ? addClass : removeClass)(document.body, "modal-open");
    },
  },
  beforeCreate() {
    const app = getCurrentInstance();
    if (app) {
      app.appContext.config.globalProperties.$modal = this;
    }
  },
  methods: {
    close(modalId?, destroy?): Promise<void> {
      modalId = modalId || this.currModalId;

      let modal;

      if (!modalId || !(modal = this.getModal(modalId))) {
        return Promise.resolve();
      }

      const { options } = modal;

      options.show = false;

      const modalItem = this.getModalItem(modalId);

      if (!modalItem) {
        return Promise.reject(new TypeError(NON_TRANSITION_ERR));
      }

      const callback = (resolve) => {
        options.destroy || destroy
          ? this.removeModal(modalId)
          : this.resetCurrModal(modalId);
        resolve();
      };

      return new Promise((resolve) => {
        callback(resolve);
        if (typeof modalItem["onAfterLeave"] == "function") {
          modalItem["onAfterLeave"]();
        }
      });
    },
    closeAll(destroy = true, immediate = false): Promise<unknown> {
      let promise = Promise.resolve();

      destroy && immediate
        ? (this.modals = [])
        : this.modals.forEach((modal) => {
            promise = promise.then(() => this.close(modal.id, destroy));
          });

      return promise;
    },
    onclose(data) {
      this.dataResolve(data);
      this.close();
    },
    open(modal: iModals) {
      modal.id = modal.id || Date.now().toString();
      return isPromise(modal.component)
        ? modal.component.then((component) =>
            this.resolve(
              Object.assign(modal, {
                component: component["default"] || component,
              }),
            ),
          )
        : this.resolve(modal);
    },
    resolve(modal: iModals) {
      const { id, component, props, options } = modal;

      const m = this.getModal(id);

      if (m) {
        component && (m.component = markRaw(component));
        modal = m;
      } else if (!component) {
        return Promise.reject(
          new ReferenceError("no component passed on initialization"),
        );
      }

      modal.props = { ...props };

      const opts = { ...DEFAULT_OPTIONS, ...options };

      if (!opts.show) {
        modal.options = opts;
        return Promise.resolve();
      }

      const promise =
        this.currModalId === id ? Promise.resolve() : this.close();

      return promise.then(async () => {
        modal.options = opts;
        // Mark component as raw to prevent Vue from making it reactive
        if ((await component) && modal.component) {
          modal.component = markRaw(modal.component);
        }
        m || this.modals.push(modal);
        this.currModal = modal;
        return new Promise((resolve, reject) => {
          this.$nextTick(() => {
            const modalItem = this.getModalItem(id);
            if (modalItem) {
              if (typeof modalItem["onAfterEnter"] == "function") {
                modalItem["onAfterEnter"]();
              }
              this.dataResolve = resolve;
            } else {
              reject(new TypeError(NON_TRANSITION_ERR));
            }
          });
        });
      });
    },
    getModal(modalId: string) {
      return this.modals.find((m) => m.id === modalId);
    },
    getModalIndex(modalId: string) {
      return this.modals.findIndex((m) => m.id === modalId);
    },
    getModalRef(modalId: string) {
      return this.divs[modalId];
    },
    getModalItem(modalId: string) {
      const modalRef = this.getModalRef(modalId);
      return modalRef; //&& modalRef.$children[0];
    },
    resetCurrModal(modalId: string): void {
      if (modalId === this.currModalId) {
        this.currModal = undefined;
      }
    },
    removeModal(modalId: string): void {
      const modalIndex = this.getModalIndex(modalId);
      modalIndex + 1 && this.modals.splice(modalIndex, 1);
      this.resetCurrModal(modalId);
    },
    handleBackdrop(e, id, backdrop) {
      if (
        e.target !== e.currentTarget ||
        backdrop === "static" ||
        e.target.className === "modal-container"
      ) {
        return;
      }
      this.close(id);
    },
  },
});
</script>
<style lang="scss" src="./modal.global.scss"></style>
